import React, { useMemo, useState } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { cn } from '@/utils/cn';
import { Badge } from '@/components/UI/Badge';
import { Button } from '@/components/UI/Button';
import { EmptyState } from '@/components/UI/EmptyState';
import { AgentFormValues, SectionCard, screenMeta, TENANT_ROLES } from './agentShared';
import { classifyPrompts, type PromptKind } from './promptPlacement';

const KIND_LABEL: Record<PromptKind, string> = {
  global: 'Global', general: 'General', specific: 'Specific', card: 'Card', legacy: 'Legacy',
};
const KIND_HINT: Record<PromptKind, string> = {
  global: 'Sent on every turn',
  general: 'Sent on every turn (kept with its screen for editing)',
  specific: 'Sent only while its screen is open',
  card: 'Sent only while its card or section is the one in use',
  legacy: 'Scoped to screens the old way',
};

interface Props {
  /** Jump to where the block is edited (Instructions for global blocks, Modules for screen blocks). */
  onEdit: (kind: PromptKind, index: number) => void;
}

/**
 * Every prompt block in ONE list, in the exact order they are written into the prompt. Global, General and Specific
 * blocks can be placed anywhere relative to each other: the kind only decides WHETHER a block is sent, never WHERE.
 */
const PromptOrderTab: React.FC<Props> = ({ onEdit }) => {
  const { control, getValues } = useFormContext<AgentFormValues>();
  const { fields, move } = useFieldArray({ control, name: 'system_prompts' });
  const watchedRaw = useWatch({ control, name: 'system_prompts' });
  const watched = useMemo(() => watchedRaw ?? [], [watchedRaw]);
  const placements = useMemo(() => classifyPrompts(watched), [watched]);
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);
  const [drafts, setDrafts] = useState<Record<number, string>>({});
  const [filter, setFilter] = useState<'all' | PromptKind>('all');

  const total = fields.length;
  const roleLabel = (r: string) => TENANT_ROLES.find((t) => t.value === r)?.label ?? r;

  const moveTo = (from: number, to: number) => {
    if (from === to || to < 0 || to >= total) return;
    move(from, to);
  };

  const commitPos = (index: number) => {
    const n = Math.round(Number(drafts[index]));
    setDrafts(({ [index]: _drop, ...rest }) => rest);
    if (Number.isFinite(n) && n >= 1 && n <= total) moveTo(index, n - 1);
  };

  const counts = useMemo(() => {
    const c: Record<PromptKind, number> = { global: 0, general: 0, specific: 0, card: 0, legacy: 0 };
    placements.forEach((p) => { c[p.kind] += 1; });
    return c;
  }, [placements]);

  return (
    <SectionCard
      title="Prompt order"
      desc="All blocks, in the order they are written into the prompt. Drag a row, type a position number, or use the arrows. Global, General and Specific blocks can be mixed in any order: the kind only decides whether a block is sent (Specific ones only while their screen is open), never where it sits."
    >
      <div className="mb-3 flex flex-wrap items-center gap-1.5" role="group" aria-label="Show blocks of kind">
        {(['all', 'global', 'general', 'specific', 'card', 'legacy'] as const)
          .filter((k) => k === 'all' || counts[k] > 0)
          .map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setFilter(k)}
              aria-pressed={filter === k}
              className={cn(
                'rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors focus-ring',
                filter === k ? 'border-accent/40 bg-accent/10 text-accent-text' : 'border-ui-border bg-ui-card text-ui-text2 hover:bg-ui-hover',
              )}
            >
              {k === 'all' ? `All (${total})` : `${KIND_LABEL[k]} (${counts[k]})`}
            </button>
          ))}
        {filter !== 'all' && (
          <span className="text-xs text-ui-text3">Positions still count all {total} blocks; the others are hidden here only.</span>
        )}
      </div>

      {total === 0 ? (
        <EmptyState variant="inline" title="No prompt blocks yet." />
      ) : (
        <ol className="space-y-1.5">
          {fields.map((f, index) => {
            const block = watched[index] ?? getValues(`system_prompts.${index}`);
            const placement = placements.get(index) ?? { kind: 'global' as PromptKind, modules: [] };
            if (filter !== 'all' && placement.kind !== filter) return null;
            const roles = block?.condition_roles ?? [];
            const firstLine = (block?.prompt ?? '').split('\n').find((l) => l.trim()) ?? '';
            return (
              <li
                key={f.id}
                onDragOver={(e) => { if (dragFrom !== null) { e.preventDefault(); setDragOver(index); } }}
                onDragLeave={() => setDragOver((v) => (v === index ? null : v))}
                onDrop={(e) => { e.preventDefault(); if (dragFrom !== null) moveTo(dragFrom, index); setDragFrom(null); setDragOver(null); }}
                className={cn(
                  'flex items-center gap-2 rounded-control border bg-ui-card px-2.5 py-2 transition-colors',
                  dragOver === index && dragFrom !== null && dragFrom !== index ? 'border-accent ring-2 ring-ui-ring/40' : 'border-ui-border',
                  dragFrom === index && 'opacity-50',
                )}
              >
                <span
                  draggable
                  onDragStart={(e) => { setDragFrom(index); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(index)); }}
                  onDragEnd={() => { setDragFrom(null); setDragOver(null); }}
                  title="Drag to reorder"
                  aria-label={`Drag block ${index + 1} to reorder`}
                  className="shrink-0 cursor-grab select-none px-0.5 text-base leading-none text-ui-text3 hover:text-ui-text2 active:cursor-grabbing"
                >⠿</span>
                <input
                  type="number" min={1} max={total} inputMode="numeric"
                  aria-label={`Position of block ${index + 1} of ${total}`}
                  title="Type a number to move this block to that position"
                  value={drafts[index] ?? String(index + 1)}
                  onChange={(e) => setDrafts((d) => ({ ...d, [index]: e.target.value }))}
                  onFocus={(e) => e.currentTarget.select()}
                  onBlur={() => commitPos(index)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') e.currentTarget.blur();
                    if (e.key === 'Escape') { setDrafts(({ [index]: _d, ...rest }) => rest); e.currentTarget.blur(); }
                  }}
                  className="h-6 w-10 shrink-0 rounded-full border border-ui-border bg-ui-subtle text-center text-xs font-semibold text-ui-text [appearance:textfield] focus:outline-none focus:ring-2 focus:ring-ui-ring [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                />
                <div className="flex shrink-0 flex-col gap-0.5">
                  <button type="button" onClick={() => moveTo(index, index - 1)} disabled={index === 0} title="Move up" aria-label="Move block up"
                    className="rounded px-1 text-xs leading-none text-ui-text3 hover:bg-ui-hover hover:text-ui-text focus-ring disabled:opacity-25">▲</button>
                  <button type="button" onClick={() => moveTo(index, index + 1)} disabled={index === total - 1} title="Move down" aria-label="Move block down"
                    className="rounded px-1 text-xs leading-none text-ui-text3 hover:bg-ui-hover hover:text-ui-text focus-ring disabled:opacity-25">▼</button>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="truncate text-sm font-medium text-ui-text">{block?.title || 'Untitled block'}</span>
                    <Badge variant={placement.kind === 'specific' || placement.kind === 'card' ? 'accent' : placement.kind === 'general' ? 'steel' : 'neutral'} title={KIND_HINT[placement.kind]}>
                      {KIND_LABEL[placement.kind]}
                      {placement.modules.length > 0 ? ` · ${placement.modules.map((m) => screenMeta(m).label).join(', ')}` : ''}
                      {(placement.cards ?? []).length > 0 ? ` · ${(placement.cards ?? []).join(', ')}` : ''}
                    </Badge>
                    {roles.length > 0 && <Badge variant="warning" title="Only sent to these roles">{roles.map(roleLabel).join(', ')}</Badge>}
                  </div>
                  <p className="truncate font-mono text-xs text-ui-text3">{firstLine}</p>
                </div>
                <Button type="button" variant="link" size="xs" onClick={() => onEdit(placement.kind, index)}>
                  Edit
                </Button>
              </li>
            );
          })}
        </ol>
      )}
    </SectionCard>
  );
};

export default PromptOrderTab;
